import type { IEmployeeApiPort } from '@personnel/application/ports/IEmployeeApiPort';
import type { EmployeePinValidateDto } from '@personnel/application/dtos/EmployeeRequestDto';
import type { PinValidateResponseDto } from '@personnel/application/dtos';

/**
 * Use Case for validating an employee PIN
 *
 * Orchestrates the PIN validation flow by delegating to the API port.
 *
 * @param {Object} props - Use case dependencies
 * @param {IEmployeeApiPort} props.apiPort - Port for API operations
 * @returns {Object} Use case executor with execute method
 *
 * @example
 * const useCase = validateEmployeePinUseCase({ apiPort: employeeRepository });
 * const result = await useCase.execute({ pin: '1234', userUuid: 'user-uuid' });
 * console.log(result.valid); // true or false
 */
export const validateEmployeePinUseCase = (props: { apiPort: IEmployeeApiPort }) => {
  const { apiPort } = props;

  /**
   * Executes the PIN validation
   *
   * @param {EmployeePinValidateDto} request - PIN validation request
   * @returns {Promise<PinValidateResponseDto>} PIN validation response
   */
  const execute = async (request: EmployeePinValidateDto): Promise<PinValidateResponseDto> => {
    return apiPort.validatePin(request);
  };

  return { execute };
};
